import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiHome, FiBook, FiEye, FiVideo, FiUsers, FiFile, FiArrowLeft, FiArrowRight, FiPlus, FiTrash2, FiX, FiEdit, FiImage, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import Modal from '../../components/common/Modal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { teacherCourseAPI, teacherGradeAPI, teacherSubjectAPI, teacherVideoAPI, teacherFileAPI, teacherQuizAPI } from '../../api/teacherEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  SectionTitle,
  WizardContainer,
  WizardSteps,
  StepIndicator,
  StepContent,
  StepForm,
  FormSection,
  FormRow,
  VideoUploadArea,
  FileUploadArea,
  QuizSection,
  QuestionCard,
  QuestionForm,
  OptionForm,
  OptionList,
  OptionItem,
  WizardActions,
  ProgressBar,
} from '../dashboards/Dashboard.styles';

const CourseCreationWizard = () => {
  const { theme } = useTheme();
  const { courseId: routeCourseId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [courseId, setCourseId] = useState(null);
  
  // Basic course details
  const [courseDetails, setCourseDetails] = useState({
    title: '',
    description: '',
    grade_id: '',
    subject_id: '',
    term: 'Term 1',
    pass_mark: 70,
    is_series: false,
  });

  // Videos
  const [videos, setVideos] = useState([]);

  // Files
  const [files, setFiles] = useState([]);
  
  // File input ref for clearing
  const videoInputRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  // Thumbnail
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  // Quiz
  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    pass_mark: 70,
    time_limit: 30,
  });
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    question_text: '',
    question_type: 'text',
    question_image: null,
    question_image_preview: null,
    passage_text: '',
    points: 1,
    options: []
  });
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  
  // Passage mode: track if we're adding sub-questions to an existing passage
  const [activePassageGroup, setActivePassageGroup] = useState(null);
  
  // Video preview state
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/teacher' },
    { icon: <FiUsers />, label: 'Grades', path: '/teacher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/teacher/subjects' },
  ];

  const steps = [
    { number: 1, title: 'Basic Details', description: 'Course information' },
    { number: 2, title: 'Videos', description: 'Upload video content' },
    { number: 3, title: 'Files', description: 'Upload documents' },
    { number: 4, title: 'Quiz', description: 'Create assessment' },
    { number: 5, title: 'Review', description: 'Final review' },
  ];

  useEffect(() => {
    fetchGrades();
    fetchSubjects();
    
    // Check if we're in edit mode via route parameter
    if (routeCourseId) {
      console.log('Setting edit mode with course ID from route:', routeCourseId);
      setEditMode(true);
      setCourseId(routeCourseId);
      loadCourseData(routeCourseId);
    } else {
      console.log('Not in edit mode - creating new course');
    }
  }, [routeCourseId]);

  const fetchGrades = async () => {
    try {
      const response = await teacherGradeAPI.getGrades();
      setGrades(response.data.grades || []);
    } catch (error) {
      showToast('error', 'Failed to load grades');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await teacherSubjectAPI.getSubjects();
      setSubjects(response.data.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load subjects');
    }
  };

  const loadCourseData = async (id) => {
    setLoadingCourse(true);
    try {
      console.log('Loading course with ID:', id);
      
      // Use the proper API endpoint to get individual course content
      const response = await teacherCourseAPI.getCourse(id);
      console.log('Course response:', response);
      
      const courseContent = response.data.course_content;
      console.log('Course content:', courseContent);
      
      if (!courseContent) {
        throw new Error('Course content not found in response');
      }
      
      // Populate basic details
      setCourseDetails({
        title: courseContent.title || '',
        description: courseContent.description || '',
        grade_id: courseContent.grade?.id || courseContent.grade_id || '',
        subject_id: courseContent.subject?.id || courseContent.subject_id || '',
        term: courseContent.term || 'Term 1',
        pass_mark: courseContent.pass_mark || 70,
        is_series: courseContent.is_series || false,
      });

      // Set existing thumbnail if available
      if (courseContent.thumbnail_url) {
        setThumbnailPreview(courseContent.thumbnail_url);
        setThumbnail(null); // No file object for existing thumbnail
      }

      // Load videos if they exist
      if (courseContent.videos && courseContent.videos.length > 0) {
        const videoData = courseContent.videos.map((video, index) => ({
          id: Date.now() + index, // Local ID for UI
          serverId: video.id, // Server ID for API calls
          file: null, // Can't load actual file, just metadata
          title: video.title || `Video ${index + 1}`,
          description: video.description || '',
          order_index: video.order_index || index,
        }));
        setVideos(videoData);
      }

      // Load files if they exist
      if (courseContent.files && courseContent.files.length > 0) {
        const fileData = courseContent.files.map((file, index) => ({
          id: Date.now() + index, // Local ID for UI
          serverId: file.id, // Server ID for API calls
          file: null, // Can't load actual file, just metadata
          title: file.title || `File ${index + 1}`,
          description: file.description || '',
          file_type: file.file_type || 'application/octet-stream',
        }));
        setFiles(fileData);
      }

      // Load quizzes if they exist
      if (courseContent.quizzes && courseContent.quizzes.length > 0) {
        const quiz = courseContent.quizzes[0]; // Take the first quiz
        setQuiz({
          title: quiz.title || '',
          description: quiz.description || '',
          pass_mark: quiz.pass_mark || 70,
          time_limit: quiz.time_limit || 30,
        });

        // Load questions if they exist
        if (quiz.questions && quiz.questions.length > 0) {
          const questionData = quiz.questions.map((question, index) => ({
            id: question.id || Date.now() + index,
            question_text: question.question_text || '',
            question_type: question.question_type || 'text',
            question_image: null, // Can't load file object from server
            question_image_preview: question.question_image_url || null, // Show existing image
            passage_text: question.passage_text || '',
            points: question.points || 1,
            options: question.options ? question.options.map((option, optIndex) => ({
              id: option.id || Date.now() + optIndex,
              option_text: option.option_text || '',
              option_image: null, // Can't load file object from server
              option_image_preview: option.option_image_url || null, // Show existing image
              is_correct: option.is_correct || false,
              order_index: option.order_index || optIndex,
            })) : [],
          }));
          setQuestions(questionData);
        }
      }

      showToast('success', 'Course loaded for editing');
    } catch (error) {
      console.error('Failed to load course:', error);
      console.error('Error details:', error.response?.data);
      
      // Check if it's a 404 error (course not found)
      if (error.response?.status === 404) {
        showToast('error', 'Course not found. Redirecting to courses...');
      } else {
        showToast('error', 'Failed to load course data. Redirecting to courses...');
      }
      
      // Redirect back if course not found
      setTimeout(() => {
        window.location.href = '/teacher';
      }, 2000);
    } finally {
      setLoadingCourse(false);
    }
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      // Validate basic details including thumbnail
      if (!courseDetails.title.trim()) {
        showToast('error', 'Please enter a course title');
        return;
      }
      if (!courseDetails.grade_id) {
        showToast('error', 'Please select a grade');
        return;
      }
      if (!courseDetails.subject_id) {
        showToast('error', 'Please select a subject');
        return;
      }
      if (!editMode && !thumbnail) {
        showToast('error', 'Please upload a course thumbnail');
        return;
      }
      if (editMode && !thumbnail && !thumbnailPreview) {
        showToast('error', 'Please upload a course thumbnail');
        return;
      }
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleVideoUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newVideos = selectedFiles.map((file, index) => ({
      id: Date.now() + index,
      serverId: null, // New uploads don't have server ID yet
      file,
      title: file.name.replace(/\.[^/.]+$/, ""),
      description: '',
      order_index: videos.length + index,
    }));
    setVideos(prevVideos => [...prevVideos, ...newVideos]);
    showToast('success', `${selectedFiles.length} video(s) added successfully`);
    
    // Clear the input so the same file can be selected again
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const handlePreviewVideo = (video) => {
    setPreviewVideo(video);
    setShowVideoPreview(true);
  };

  const closeVideoPreview = () => {
    setShowVideoPreview(false);
    setPreviewVideo(null);
  };

  const moveVideoUp = (index) => {
    if (index === 0) return;
    const newVideos = [...videos];
    [newVideos[index - 1], newVideos[index]] = [newVideos[index], newVideos[index - 1]];
    // Update order indices
    newVideos.forEach((video, idx) => {
      video.order_index = idx;
    });
    setVideos(newVideos);
    showToast('success', 'Video order updated');
  };

  const moveVideoDown = (index) => {
    if (index === videos.length - 1) return;
    const newVideos = [...videos];
    [newVideos[index], newVideos[index + 1]] = [newVideos[index + 1], newVideos[index]];
    // Update order indices
    newVideos.forEach((video, idx) => {
      video.order_index = idx;
    });
    setVideos(newVideos);
    showToast('success', 'Video order updated');
  };

  const updateFileAttachment = (fileId, videoIndex) => {
    setFiles(files.map(f => 
      f.id === fileId ? { ...f, attachedToVideoIndex: videoIndex } : f
    ));
  };

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const newFiles = selectedFiles.map((file, index) => ({
      id: Date.now() + index,
      serverId: null, // New uploads don't have server ID yet
      file,
      title: file.name.replace(/\.[^/.]+$/, ""),
      description: '',
      file_type: file.type,
      attachedToVideoIndex: '', // Empty means content-level file
    }));
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
    showToast('success', `${selectedFiles.length} file(s) added successfully`);
    
    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Please select a valid image file (JPG, PNG, GIF, SVG)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showToast('error', 'File size must be less than 5MB');
        return;
      }

      setThumbnail(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
    }
  };

  const removeThumbnail = () => {
    if (thumbnailPreview) {
      URL.revokeObjectURL(thumbnailPreview);
    }
    setThumbnail(null);
    setThumbnailPreview(null);
  };

  const removeVideo = async (id) => {
    try {
      // If we're in edit mode and have a courseId, delete from server
      if (editMode && courseId) {
        // Check if this is an existing video (has server ID) or new video (local ID)
        const video = videos.find(v => v.id === id);
        if (video && video.serverId) {
          // This is an existing video on the server, delete it
          await teacherVideoAPI.deleteVideo(courseId, video.serverId);
          showToast('success', 'Video deleted from server');
        }
      }
      
      // Remove from local state
      setVideos(videos.filter(v => v.id !== id));
    } catch (error) {
      console.error('Failed to delete video:', error);
      showToast('error', 'Failed to delete video. Please try again.');
    }
  };

  const removeFile = async (id) => {
    try {
      // If we're in edit mode and have a courseId, delete from server
      if (editMode && courseId) {
        // Check if this is an existing file (has server ID) or new file (local ID)
        const file = files.find(f => f.id === id);
        if (file && file.serverId) {
          // This is an existing file on the server, delete it
          await teacherFileAPI.deleteFile(courseId, file.serverId);
          showToast('success', 'File deleted from server');
        }
      }
      
      // Remove from local state
      setFiles(files.filter(f => f.id !== id));
    } catch (error) {
      console.error('Failed to delete file:', error);
      showToast('error', 'Failed to delete file. Please try again.');
    }
  };

  const addQuestion = () => {
    // Validate based on question type
    const isImageQuestion = currentQuestion.question_type === 'image';
    const isPassageQuestion = currentQuestion.question_type === 'passage' || currentQuestion.question_type === 'broken_passage';
    
    // For image questions, either question_text or question_image is required
    if (isImageQuestion && !currentQuestion.question_text.trim() && !currentQuestion.question_image) {
      showToast('error', 'Please add question text or upload an image');
      return;
    }
    
    // For passage questions, passage_text is required only for first question
    if (isPassageQuestion && !currentQuestion.passage_text.trim() && !activePassageGroup) {
      showToast('error', 'Please add passage text for passage questions');
      return;
    }
    
    // For non-image questions, question_text is required
    if (!isImageQuestion && !currentQuestion.question_text.trim()) {
      showToast('error', 'Please add question text');
      return;
    }
    
    if (currentQuestion.options.length < 2) {
      showToast('error', 'Please add at least 2 options');
      return;
    }
    
    const hasCorrectAnswer = currentQuestion.options.some(opt => opt.is_correct);
    if (!hasCorrectAnswer) {
      showToast('error', 'Please mark at least one option as correct');
      return;
    }
    
    if (editingQuestion !== null) {
      // Update existing question
      setQuestions(questions.map((q, idx) => 
        idx === editingQuestion ? { ...currentQuestion, id: q.id } : q
      ));
      setEditingQuestion(null);
      showToast('success', 'Question updated successfully');
    } else {
      // Add new question
      const newQuestion = { ...currentQuestion, id: Date.now() };
      
      // If adding sub-question to an existing passage, use the same passage_text
      if (activePassageGroup && isPassageQuestion) {
        newQuestion.passage_text = activePassageGroup;
      }
      
      setQuestions([...questions, newQuestion]);
      showToast('success', 'Question added successfully');
    }
    
    // Reset form
    setCurrentQuestion({
      question_text: '',
      question_type: 'text',
      question_image: null,
      question_image_preview: null,
      passage_text: '',
      points: 1,
      options: []
    });
    
    // Clear active passage group after adding sub-question
    if (activePassageGroup) {
      setActivePassageGroup(null);
    }
  };

  // Helper function to group questions by passage_text
  const groupQuestionsByPassage = (questionsList) => {
    const grouped = [];
    const passageMap = new Map(); // Maps passage_text to question indices
    
    questionsList.forEach((question, index) => {
      if ((question.question_type === 'passage' || question.question_type === 'broken_passage') && question.passage_text) {
        if (!passageMap.has(question.passage_text)) {
          // First occurrence of this passage
          passageMap.set(question.passage_text, grouped.length);
          grouped.push({
            type: 'passage_group',
            passage_text: question.passage_text,
            question_type: question.question_type,
            questions: [question],
            indices: [index]
          });
        } else {
          // Add question to existing passage group
          const groupIndex = passageMap.get(question.passage_text);
          grouped[groupIndex].questions.push(question);
          grouped[groupIndex].indices.push(index);
        }
      } else {
        // Regular question, add as standalone
        grouped.push({
          type: 'question',
          question: question,
          index: index
        });
      }
    });
    
    return grouped;
  };

  const handleEditQuestion = (question, index) => {
    setCurrentQuestion({ ...question });
    setEditingQuestion(index);
    showToast('info', 'Editing question. Update and click "Add Question" to save changes.');
  };

  const handleDeleteQuestion = (index) => {
    setQuestionToDelete(index);
    setShowDeleteModal(true);
  };

  const confirmDeleteQuestion = () => {
    if (questionToDelete !== null) {
      setQuestions(questions.filter((_, idx) => idx !== questionToDelete));
      setShowDeleteModal(false);
      setQuestionToDelete(null);
      showToast('success', 'Question deleted successfully');
    }
  };

  const addOption = () => {
    if (currentQuestion.options.length < 6) {
      setCurrentQuestion({
        ...currentQuestion,
        options: [...currentQuestion.options, {
          id: Date.now(),
          option_text: '',
          option_image: null,
          option_image_preview: null,
          is_correct: false,
          order_index: currentQuestion.options.length
        }]
      });
    }
  };

  const removeOption = (optionId) => {
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options.filter(opt => opt.id !== optionId)
    });
  };

  const updateOption = (optionId, field, value) => {
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options.map(opt => 
        opt.id === optionId ? { ...opt, [field]: value } : opt
      )
    });
  };

  const handleQuestionImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Please select a valid image file (JPG, PNG, GIF, SVG)');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', 'Image size must be less than 10MB');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setCurrentQuestion({
        ...currentQuestion,
        question_image: file,
        question_image_preview: previewUrl
      });
      showToast('success', 'Question image added');
    }
  };

  const removeQuestionImage = () => {
    if (currentQuestion.question_image_preview) {
      URL.revokeObjectURL(currentQuestion.question_image_preview);
    }
    setCurrentQuestion({
      ...currentQuestion,
      question_image: null,
      question_image_preview: null
    });
  };

  const handleOptionImageUpload = (optionId, e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        showToast('error', 'Please select a valid image file (JPG, PNG, GIF, SVG)');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('error', 'Image size must be less than 10MB');
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setCurrentQuestion({
        ...currentQuestion,
        options: currentQuestion.options.map(opt => 
          opt.id === optionId ? { 
            ...opt, 
            option_image: file, 
            option_image_preview: previewUrl 
          } : opt
        )
      });
      showToast('success', 'Option image added');
    }
  };

  const removeOptionImage = (optionId) => {
    const option = currentQuestion.options.find(opt => opt.id === optionId);
    if (option?.option_image_preview) {
      URL.revokeObjectURL(option.option_image_preview);
    }
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options.map(opt => 
        opt.id === optionId ? { 
          ...opt, 
          option_image: null, 
          option_image_preview: null 
        } : opt
      )
    });
  };

  const handleCreateCourse = async () => {
    if (!editMode && questions.length < 5) {
      showToast('error', 'Please add at least 5 questions to the quiz');
      return;
    }

    setLoading(true);
    setUploading(true);
    setUploadProgress(0);
    try {
      // Create comprehensive FormData with all course content
      const formData = new FormData();
      
      // Basic course details
      formData.append('title', courseDetails.title);
      formData.append('description', courseDetails.description || '');
      formData.append('grade_id', courseDetails.grade_id);
      formData.append('subject_id', courseDetails.subject_id);
      formData.append('is_series', courseDetails.is_series);
      formData.append('term', courseDetails.term);
      formData.append('pass_mark', courseDetails.pass_mark);
      
      // Add thumbnail (required for new courses, optional for updates)
      if (thumbnail) {
        formData.append('thumbnail', thumbnail);
        console.log('FormData - Thumbnail added:', thumbnail.name);
      }

      console.log('FormData - Basic details:', {
        title: courseDetails.title,
        description: courseDetails.description,
        grade_id: courseDetails.grade_id,
        is_series: courseDetails.is_series,
        term: courseDetails.term,
        pass_mark: courseDetails.pass_mark
      });

      // Add videos (only new uploads, not existing server videos)
      const newVideos = videos.filter(v => v.file && !v.serverId);
      newVideos.forEach((video, index) => {
        formData.append('videos', video.file);
        formData.append('video_titles', video.title);
        formData.append('video_descriptions', video.description || '');
        formData.append('video_order_indices', video.order_index || index);
        console.log(`FormData - Video ${index + 1}:`, {
          file: video.file?.name || 'No file',
          title: video.title,
          description: video.description,
          order_index: video.order_index || index,
          size: video.file?.size || 'Unknown'
        });
      });

      // Add files (only new uploads, not existing server files)
      const newFiles = files.filter(f => f.file && !f.serverId);
      newFiles.forEach((file, index) => {
        formData.append('files', file.file);
        formData.append('file_titles', file.title);
        formData.append('file_descriptions', file.description || '');
        // Add file attachment to video (empty string means content-level file)
        formData.append('file_attach_video_indices', file.attachedToVideoIndex || '');
        console.log(`FormData - File ${index + 1}:`, {
          file: file.file?.name || 'No file',
          title: file.title,
          description: file.description,
          attachedToVideo: file.attachedToVideoIndex || 'Content-level',
          type: file.file?.type || 'Unknown',
          size: file.file?.size || 'Unknown'
        });
      });

      // Prepare quiz data with questions and options (only if we have questions)
      if (questions.length > 0) {
        // Detect if all passage/broken_passage questions share the same passage_text
        const passageGroups = groupQuestionsByPassage(questions);
        const passageGroupItems = passageGroups.filter(item => item.type === 'passage_group');
        
        // Check if there's a single passage group with all questions sharing the same passage
        let sharedPassageText = null;
        if (passageGroupItems.length === 1) {
          const singlePassageGroup = passageGroupItems[0];
          // If all passage questions are in one group, use quiz-level passage
          if (singlePassageGroup.questions.length > 1) {
            sharedPassageText = singlePassageGroup.passage_text;
          }
        }
        
        const quizData = [{
          title: quiz.title,
          description: quiz.description || '',
          time_limit: quiz.time_limit,
          pass_mark: quiz.pass_mark,
          passage_text: sharedPassageText || undefined,
          questions: questions.map((question, index) => ({
            question_text: question.question_text || '',
            question_type: question.question_type,
            passage_text: sharedPassageText && (question.question_type === 'passage' || question.question_type === 'broken_passage') 
              ? null 
              : (question.passage_text || null),
            points: question.points || 1,
            options: question.options.map((option, optIndex) => ({
              option_text: option.option_text || '',
              is_correct: option.is_correct
            }))
          }))
        }];

        // Add quiz as JSON string
        formData.append('quizzes', JSON.stringify(quizData));
        
        // Add question images
        questions.forEach((question, qIndex) => {
          if (question.question_image) {
            formData.append(`question_image_${qIndex}`, question.question_image);
            console.log(`FormData - Question ${qIndex} image:`, question.question_image.name);
          }
          
          // Add option images
          question.options.forEach((option, oIndex) => {
            if (option.option_image) {
              formData.append(`option_image_${qIndex}_${oIndex}`, option.option_image);
              console.log(`FormData - Option ${qIndex}_${oIndex} image:`, option.option_image.name);
            }
          });
        });
        
        console.log('FormData - Quiz data:', {
          quizCount: quizData.length,
          questionCount: questions.length,
          quizTitle: quiz.title
        });
      }

      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }

      // Progress callback for upload tracking
      const handleUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        // Cap at 99% to show processing is still happening
        const displayProgress = Math.min(percentCompleted, 99);
        setUploadProgress(displayProgress);
        console.log(`Upload progress: ${displayProgress}%`);
      };

      // Make API call to create or update course
      let response;
      if (editMode && courseId) {
        console.log(`Updating course ${courseId} with FormData`);
        response = await teacherCourseAPI.updateCourse(courseId, formData, handleUploadProgress);
        // Set to 100% after successful completion
        setUploadProgress(100);
        showToast('success', 'Course updated successfully!');
      } else {
        console.log('Creating new course with FormData');
        response = await teacherCourseAPI.createCourse(formData, handleUploadProgress);
        // Set to 100% after successful completion
        setUploadProgress(100);
        showToast('success', 'Course created successfully!');
      }
      
      // Navigate back to courses after a short delay
      setTimeout(() => {
        window.location.href = '/teacher';
      }, 1500);
    } catch (error) {
      console.error('Course save error:', error);
      showToast('error', error.response?.data?.error || `Failed to ${editMode ? 'update' : 'create'} course`);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormSection>
            <h3>Basic Course Information</h3>
            <Input
              label="Course Title"
              type="text"
              value={courseDetails.title}
              onChange={(e) => setCourseDetails({ ...courseDetails, title: e.target.value })}
              placeholder="e.g., Mathematics - Addition and Subtraction"
              required
            />
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Description
              </label>
              <textarea
                value={courseDetails.description}
                onChange={(e) => setCourseDetails({ ...courseDetails, description: e.target.value })}
                placeholder="Brief description of the course content"
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <FormRow>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Grade *
                </label>
                <select
                  value={courseDetails.grade_id}
                  onChange={(e) => setCourseDetails({ ...courseDetails, grade_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                  }}
                  required
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Subject *
                </label>
                <select
                  value={courseDetails.subject_id}
                  onChange={(e) => setCourseDetails({ ...courseDetails, subject_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                  }}
                  required
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
                {subjects.length === 0 && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6B7280' }}>
                    No subjects available. <a href="/teacher/subjects" style={{ color: '#2c5f34', textDecoration: 'underline' }}>Create one first</a>.
                  </p>
                )}
              </div>
            </FormRow>

            <FormRow>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Term
                </label>
                <select
                  value={courseDetails.term}
                  onChange={(e) => setCourseDetails({ ...courseDetails, term: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                  }}
                >
                  <option value="Term 1">Term 1</option>
                  <option value="Term 2">Term 2</option>
                  <option value="Term 3">Term 3</option>
                </select>
              </div>
            </FormRow>

            <FormRow>
              <Input
                label="Pass Mark (%)"
                type="number"
                min="0"
                max="100"
                value={courseDetails.pass_mark}
                onChange={(e) => setCourseDetails({ ...courseDetails, pass_mark: parseInt(e.target.value) })}
                required
              />

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
                  <input
                    type="checkbox"
                    checked={courseDetails.is_series}
                    onChange={(e) => setCourseDetails({ ...courseDetails, is_series: e.target.checked })}
                  />
                  <span>This is a series course</span>
                </label>
              </div>
            </FormRow>

            {/* Course Thumbnail Upload */}
            <div style={{ marginTop: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Course Thumbnail {!editMode && <span style={{ color: '#EF4444' }}>*</span>}
                {editMode && <span style={{ color: '#6B7280', fontSize: '12px' }}> (optional - upload to update)</span>}
              </label>
              <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                Upload a course banner image (JPG, PNG, GIF, SVG). Recommended size: 1200x630px. Max 5MB.
              </p>
              
              {thumbnailPreview ? (
                <div style={{ 
                  position: 'relative', 
                  display: 'inline-block',
                  marginBottom: '12px'
                }}>
                  <img 
                    src={thumbnailPreview} 
                    alt="Course thumbnail preview"
                    style={{
                      width: '200px',
                      height: '105px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #E5E7EB'
                    }}
                  />
                  <button
                    type="button"
                    onClick={removeThumbnail}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px'
                    }}
                  >
                    ×
                  </button>
                  {editMode && !thumbnail && (
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '4px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px'
                    }}>
                      Current thumbnail
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  border: '2px dashed #D1D5DB',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  backgroundColor: '#F9FAFB',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s'
                }}
                onClick={() => document.getElementById('thumbnail-upload').click()}
                onMouseEnter={(e) => e.target.style.borderColor = '#2c5f34'}
                onMouseLeave={(e) => e.target.style.borderColor = '#D1D5DB'}
                >
                  <FiImage style={{ fontSize: '32px', color: '#9CA3AF', marginBottom: '8px' }} />
                  <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                    Click to upload thumbnail
                  </p>
                  <p style={{ margin: '4px 0 0 0', color: '#9CA3AF', fontSize: '12px' }}>
                    JPG, PNG, GIF, SVG up to 5MB
                  </p>
                </div>
              )}
              
              <input
                id="thumbnail-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                onChange={handleThumbnailUpload}
                style={{ display: 'none' }}
                required={!editMode} // Required for new courses, optional for updates
              />
            </div>
          </FormSection>
        );

      case 2:
        return (
          <FormSection>
            <h3>{editMode ? 'Manage Videos' : 'Upload Videos'}</h3>
            {editMode && (
              <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '14px' }}>
                You can add new videos or manage existing ones. To replace existing videos, delete them first and upload new ones.
              </p>
            )}
            <VideoUploadArea>
              <input
                ref={videoInputRef}
                type="file"
                multiple
                accept="video/*"
                onChange={handleVideoUpload}
                style={{ display: 'none' }}
                id="video-upload"
              />
              <label htmlFor="video-upload" style={{ cursor: 'pointer' }}>
                <FiVideo size={48} />
                <h4>{editMode ? 'Add New Videos' : 'Upload Video Files'}</h4>
                <p>Click to select video files or drag and drop</p>
                <Button variant="outline">Choose Videos</Button>
              </label>
            </VideoUploadArea>

            {videos.length > 0 && (
              <div>
                <h4>Videos ({videos.length})</h4>
                {videos.map((video, index) => (
                  <div key={video.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    padding: '12px',
                    background: video.serverId ? '#F0F9FF' : '#F9FAFB',
                    border: video.serverId ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    {/* Order Controls */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px',
                      marginRight: '4px'
                    }}>
                      <Button 
                        variant="ghost" 
                        size="small" 
                        onClick={() => moveVideoUp(index)} 
                        disabled={index === 0}
                        title="Move up"
                        style={{ 
                          padding: '4px', 
                          minWidth: 'auto',
                          opacity: index === 0 ? 0.3 : 1
                        }}
                      >
                        <FiArrowUp size={14} />
                      </Button>
                      <span style={{ 
                        fontSize: '12px', 
                        fontWeight: '600',
                        textAlign: 'center',
                        color: '#6B7280'
                      }}>
                        {index + 1}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="small" 
                        onClick={() => moveVideoDown(index)} 
                        disabled={index === videos.length - 1}
                        title="Move down"
                        style={{ 
                          padding: '4px', 
                          minWidth: 'auto',
                          opacity: index === videos.length - 1 ? 0.3 : 1
                        }}
                      >
                        <FiArrowDown size={14} />
                      </Button>
                    </div>
                    
                    <FiVideo style={{ color: '#EF4444' }} />
                    <div style={{ flex: 1 }}>
                      <Input
                        value={video.title}
                        onChange={(e) => setVideos(videos.map(v => 
                          v.id === video.id ? { ...v, title: e.target.value } : v
                        ))}
                        placeholder="Video title"
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {video.serverId && (
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#3B82F6', 
                          fontWeight: '500',
                          background: '#DBEAFE',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}>
                          Existing
                        </span>
                      )}
                      {video.file && (
                        <Button variant="ghost" size="small" onClick={() => handlePreviewVideo(video)} title="Preview video">
                          <FiEye />
                        </Button>
                      )}
                      <Button variant="ghost" size="small" onClick={() => removeVideo(video.id)}>
                        <FiTrash2 />
                      </Button>
                    </div>
                  </div>
                ))}
                <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                  {editMode ? 'Blue border indicates existing videos from server. To replace them, delete and upload new ones.' : 'Use the arrow buttons to reorder videos.'}
                </p>
              </div>
            )}
          </FormSection>
        );

      case 3:
        return (
          <FormSection>
            <h3>{editMode ? 'Manage Files' : 'Upload Files'}</h3>
            {editMode && (
              <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '14px' }}>
                You can add new files or manage existing ones. To replace existing files, delete them first and upload new ones.
              </p>
            )}
            <FileUploadArea>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.png,.gif,.svg"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                <FiFile size={48} />
                <h4>{editMode ? 'Add New Files' : 'Upload Files'}</h4>
                <p>Click to select files or drag and drop</p>
                <Button variant="outline">Choose Files</Button>
              </label>
            </FileUploadArea>

            {files.length > 0 && (
              <div>
                <h4>Files ({files.length})</h4>
                {files.map((file) => (
                  <div key={file.id} style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '8px', 
                    padding: '12px',
                    background: file.serverId ? '#F0F9FF' : '#F9FAFB',
                    border: file.serverId ? '1px solid #3B82F6' : '1px solid #E5E7EB',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <FiFile style={{ color: '#3B82F6' }} />
                      <div style={{ flex: 1 }}>
                        <Input
                          value={file.title}
                          onChange={(e) => setFiles(files.map(f => 
                            f.id === file.id ? { ...f, title: e.target.value } : f
                          ))}
                          placeholder="File title"
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {file.serverId && (
                          <span style={{ 
                            fontSize: '12px', 
                            color: '#3B82F6', 
                            fontWeight: '500',
                            background: '#DBEAFE',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Existing
                          </span>
                        )}
                        <Button variant="ghost" size="small" onClick={() => removeFile(file.id)}>
                          <FiTrash2 />
                        </Button>
                      </div>
                    </div>
                    
                    {/* File attachment to video */}
                    {!file.serverId && (
                      <div style={{ paddingLeft: '32px' }}>
                        <label style={{ 
                          fontSize: '13px', 
                          color: theme.colors.textSecondary,
                          display: 'block',
                          marginBottom: '4px'
                        }}>
                          Show this file after:
                        </label>
                        <select
                          value={file.attachedToVideoIndex}
                          onChange={(e) => updateFileAttachment(file.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${theme.colors.border}`,
                            background: theme.colors.backgroundPrimary,
                            color: theme.colors.textPrimary,
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="">Course content (not attached to video)</option>
                          {videos.map((video, vIndex) => (
                            <option key={vIndex} value={vIndex}>
                              Video {vIndex + 1}: {video.title || 'Untitled'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
                <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                  {editMode ? 'Blue border indicates existing files from server. To replace them, delete and upload new ones.' : 'Upload your document files. Optionally attach them to specific videos.'}
                </p>
              </div>
            )}
          </FormSection>
        );

      case 4:
        return (
          <FormSection>
            <h3>Create Quiz</h3>
            
            <FormRow>
              <Input
                label="Quiz Title"
                type="text"
                value={quiz.title}
                onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                placeholder="e.g., Addition Quiz"
                required
              />
              <Input
                label="Time Limit (minutes)"
                type="number"
                min="1"
                value={quiz.time_limit}
                onChange={(e) => setQuiz({ ...quiz, time_limit: parseInt(e.target.value) })}
                required
              />
            </FormRow>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                Quiz Description
              </label>
              <textarea
                value={quiz.description}
                onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
                placeholder="Brief description of the quiz"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <Input
              label="Quiz Pass Mark (%)"
              type="number"
              min="0"
              max="100"
              value={quiz.pass_mark}
              onChange={(e) => setQuiz({ ...quiz, pass_mark: parseInt(e.target.value) })}
              required
            />

            <QuizSection>
              <h4>Add Questions ({questions.length}/5 minimum)</h4>
              
              <QuestionForm>
                <FormRow>
                  <Input
                    label="Points"
                    type="number"
                    min="1"
                    value={currentQuestion.points}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, points: parseInt(e.target.value) })}
                    required
                  />
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Question Type
                    </label>
                    <select
                      value={currentQuestion.question_type}
                      onChange={(e) => setCurrentQuestion({ ...currentQuestion, question_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #E5E7EB',
                        borderRadius: '12px',
                        fontSize: '16px',
                      }}
                    >
                      <option value="text">Text Question</option>
                      <option value="image">Image Question</option>
                      <option value="passage">Passage Question (Linguistics)</option>
                      <option value="broken_passage">Broken Passage (Fill-in-the-Blank)</option>
                    </select>
                  </div>
                </FormRow>

                {/* Passage Text Field for passage and broken_passage types */}
                {(currentQuestion.question_type === 'passage' || currentQuestion.question_type === 'broken_passage') && (
                  <div>
                    {activePassageGroup ? (
                      <div style={{ 
                        background: '#EEF2FF', 
                        padding: '12px', 
                        borderRadius: '8px',
                        border: '1px solid #2c5f34',
                        marginBottom: '12px'
                      }}>
                        <p style={{ margin: 0, fontSize: '12px', color: '#2c5f34', fontWeight: '600' }}>
                          ✓ Adding sub-question to existing passage
                        </p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#374151' }}>
                          {activePassageGroup.length > 200 ? `${activePassageGroup.substring(0, 200)}...` : activePassageGroup}
                        </p>
                      </div>
                    ) : (
                      <>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                          Passage Text {currentQuestion.question_type === 'broken_passage' && <span style={{ color: '#6B7280', fontSize: '12px' }}>(Use [1], [2], etc. for blanks)</span>}
                        </label>
                        <textarea
                          value={currentQuestion.passage_text}
                          onChange={(e) => setCurrentQuestion({ ...currentQuestion, passage_text: e.target.value })}
                          placeholder={currentQuestion.question_type === 'broken_passage' 
                            ? "Enter passage with blanks marked as [1], [2], etc. Example: The capital of Kenya is [1]. It has [2] million residents."
                            : "Enter the passage text that students will read before answering questions..."}
                          rows={5}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '2px solid #E5E7EB',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontFamily: 'inherit',
                          }}
                        />
                        <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                          {currentQuestion.question_type === 'broken_passage' 
                            ? "💡 The passage text will be included when generating AI explanations to provide context."
                            : "💡 This passage will be shown to students and included in AI explanations for context."}
                        </p>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Question Text {currentQuestion.question_type === 'image' && <span style={{ color: '#6B7280', fontSize: '12px' }}>(optional if image is provided)</span>}
                  </label>
                  <textarea
                    value={currentQuestion.question_text}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, question_text: e.target.value })}
                    placeholder={currentQuestion.question_type === 'broken_passage' 
                      ? "Enter the question for this blank (e.g., 'Fill in blank [1]:')"
                      : "Enter your question here..."}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                {/* Question Image Upload for image type */}
                {currentQuestion.question_type === 'image' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                      Question Image
                    </label>
                    <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
                      Upload an image for the question (diagrams, charts, problem statements, etc.). Max 10MB.
                    </p>
                    
                    {currentQuestion.question_image_preview ? (
                      <div style={{ 
                        position: 'relative', 
                        display: 'inline-block',
                        marginBottom: '12px'
                      }}>
                        <img 
                          src={currentQuestion.question_image_preview} 
                          alt="Question preview"
                          style={{
                            maxWidth: '300px',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            border: '2px solid #E5E7EB'
                          }}
                        />
                        <button
                          type="button"
                          onClick={removeQuestionImage}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div style={{
                        border: '2px dashed #D1D5DB',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#F9FAFB',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s'
                      }}
                      onClick={() => document.getElementById('question-image-upload').click()}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = '#2c5f34'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = '#D1D5DB'}
                      >
                        <FiImage style={{ fontSize: '32px', color: '#9CA3AF', marginBottom: '8px' }} />
                        <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                          Click to upload question image
                        </p>
                      </div>
                    )}
                    
                    <input
                      id="question-image-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                      onChange={handleQuestionImageUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                )}

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h5>Options ({currentQuestion.options.length})</h5>
                    <Button size="small" onClick={addOption} disabled={currentQuestion.options.length >= 6}>
                      <FiPlus /> Add Option
                    </Button>
                  </div>

                  <OptionList>
                    {currentQuestion.options.map((option, index) => (
                      <OptionItem key={option.id} style={{ flexDirection: 'column', alignItems: 'stretch', padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <input
                            type="radio"
                            name={`correct-${currentQuestion.id}`}
                            checked={option.is_correct}
                            onChange={() => {
                              setCurrentQuestion({
                                ...currentQuestion,
                                options: currentQuestion.options.map(opt => 
                                  opt.id === option.id ? { ...opt, is_correct: true } : { ...opt, is_correct: false }
                                )
                              });
                            }}
                          />
                          <Input
                            value={option.option_text}
                            onChange={(e) => updateOption(option.id, 'option_text', e.target.value)}
                            placeholder={`Option ${index + 1} text`}
                            size="small"
                          />
                          <Button 
                            variant="ghost" 
                            size="small" 
                            onClick={() => document.getElementById(`option-image-${option.id}`).click()}
                            title="Upload image for this option"
                          >
                            <FiImage />
                          </Button>
                          <Button variant="ghost" size="small" onClick={() => removeOption(option.id)}>
                            <FiX />
                          </Button>
                        </div>
                        
                        {/* Option Image Preview */}
                        {option.option_image_preview && (
                          <div style={{ 
                            marginTop: '8px',
                            marginLeft: '24px',
                            position: 'relative', 
                            display: 'inline-block'
                          }}>
                            <img 
                              src={option.option_image_preview} 
                              alt={`Option ${index + 1}`}
                              style={{
                                maxWidth: '150px',
                                maxHeight: '100px',
                                objectFit: 'contain',
                                borderRadius: '4px',
                                border: '1px solid #E5E7EB'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeOptionImage(option.id)}
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '-8px',
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )}
                        
                        {/* Hidden file input for option image */}
                        <input
                          id={`option-image-${option.id}`}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                          onChange={(e) => handleOptionImageUpload(option.id, e)}
                          style={{ display: 'none' }}
                        />
                      </OptionItem>
                    ))}
                  </OptionList>

                  {currentQuestion.options.length < 6 && (
                    <div style={{ marginBottom: '12px' }}>
                      <Button variant="outline" size="small" onClick={addOption}>
                        <FiPlus /> Add Another Option
                      </Button>
                    </div>
                  )}

                  <Button onClick={addQuestion} disabled={currentQuestion.question_text.trim() === '' || currentQuestion.options.length < 2}>
                    <FiPlus /> {editingQuestion !== null ? 'Update Question' : 'Add Question'}
                  </Button>
                </div>
              </QuestionForm>

              {questions.length > 0 && (
                <div>
                  <h4>{editMode ? 'Existing Questions' : 'Added Questions'} ({questions.length})</h4>
                  {groupQuestionsByPassage(questions).map((item, groupIndex) => {
                    // If it's a passage group, display it differently
                    if (item.type === 'passage_group') {
                      return (
                        <QuestionCard key={`passage-group-${groupIndex}`} style={{ border: '2px solid #2c5f34' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ 
                              background: '#EEF2FF', 
                              padding: '12px', 
                              borderRadius: '8px',
                              borderLeft: '4px solid #2c5f34'
                            }}>
                              <p style={{ margin: 0, fontSize: '12px', color: '#2c5f34', fontWeight: '700', textTransform: 'uppercase' }}>
                                📖 {item.question_type === 'broken_passage' ? 'Broken Passage' : 'Passage'} ({item.questions.length} questions)
                              </p>
                              <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#374151' }}>
                                {item.passage_text.length > 200 ? `${item.passage_text.substring(0, 200)}...` : item.passage_text}
                              </p>
                            </div>
                            
                            {item.questions.map((question, qIndex) => (
                              <div key={question.id} style={{ 
                                background: '#F9FAFB', 
                                padding: '12px', 
                                borderRadius: '8px',
                                marginLeft: '16px'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                  <div style={{ flex: 1 }}>
                                    <h5 style={{ fontSize: '14px', margin: '0 0 4px 0' }}>
                                      Question {qIndex + 1}: {question.question_text || '(Image Question)'}
                                    </h5>
                                    <p style={{ margin: '4px 0', color: '#6B7280', fontSize: '13px' }}>
                                      Points: {question.points} | Options: {question.options.length}
                                    </p>
                                    
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                                      {question.options.map((opt, idx) => (
                                        <span 
                                          key={idx}
                                          style={{ 
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            background: opt.is_correct ? '#D1FAE5' : '#F3F4F6',
                                            color: opt.is_correct ? '#065F46' : '#374151',
                                            fontWeight: opt.is_correct ? '600' : 'normal'
                                          }}
                                        >
                                          {opt.is_correct && '✓ '}{opt.option_text || `(Image Option ${idx + 1})`}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <Button variant="ghost" size="small" onClick={() => handleEditQuestion(question, item.indices[qIndex])} title="Edit Question">
                                      <FiEdit />
                                    </Button>
                                    <Button variant="ghost" size="small" onClick={() => handleDeleteQuestion(item.indices[qIndex])} title="Delete Question">
                                      <FiTrash2 />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {/* Add Sub-Question button */}
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                              <Button 
                                variant="outline" 
                                size="small" 
                                onClick={() => {
                                  setActivePassageGroup(item.passage_text);
                                  setCurrentQuestion({
                                    ...currentQuestion,
                                    question_type: item.question_type,
                                    passage_text: ''
                                  });
                                }}
                              >
                                <FiPlus /> Add Another Question to This Passage
                              </Button>
                            </div>
                          </div>
                        </QuestionCard>
                      );
                    }
                    
                    // Regular standalone question
                    const question = item.question;
                    const index = item.index;
                    return (
                    <QuestionCard key={question.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <h5>Q{index + 1}: {question.question_text || '(Image Question)'}</h5>
                          <p style={{ margin: '4px 0', color: '#6B7280', fontSize: '14px' }}>
                            Points: {question.points} | Options: {question.options.length} | Type: {question.question_type}
                          </p>
                          
                          {/* Show question image if present */}
                          {question.question_image_preview && (
                            <div style={{ marginTop: '8px' }}>
                              <img 
                                src={question.question_image_preview} 
                                alt="Question"
                                style={{
                                  maxWidth: '200px',
                                  maxHeight: '120px',
                                  objectFit: 'contain',
                                  borderRadius: '4px',
                                  border: '1px solid #E5E7EB'
                                }}
                              />
                            </div>
                          )}
                          
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                            {question.options.map((opt, idx) => (
                              <div 
                                key={idx}
                                style={{ 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  gap: '4px'
                                }}
                              >
                                <span 
                                  style={{ 
                                    padding: '4px 8px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    background: opt.is_correct ? '#D1FAE5' : '#F3F4F6',
                                    color: opt.is_correct ? '#065F46' : '#374151',
                                    fontWeight: opt.is_correct ? '600' : 'normal'
                                  }}
                                >
                                  {opt.is_correct && '✓ '}{opt.option_text || `(Image Option ${idx + 1})`}
                                </span>
                                {opt.option_image_preview && (
                                  <img 
                                    src={opt.option_image_preview} 
                                    alt={`Option ${idx + 1}`}
                                    style={{
                                      maxWidth: '80px',
                                      maxHeight: '60px',
                                      objectFit: 'contain',
                                      borderRadius: '4px',
                                      border: '1px solid #E5E7EB'
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <Button variant="ghost" size="small" onClick={() => handleEditQuestion(question, index)} title="Edit Question">
                            <FiEdit />
                          </Button>
                          <Button variant="ghost" size="small" onClick={() => handleDeleteQuestion(index)} title="Delete Question">
                            <FiTrash2 />
                          </Button>
                        </div>
                      </div>
                    </QuestionCard>
                    );
                  })}
                  {editMode && (
                    <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                      Note: You can edit existing questions or add new ones. Changes will be saved when you update the course.
                    </p>
                  )}
                </div>
              )}
            </QuizSection>
          </FormSection>
        );

      case 5:
        return (
          <FormSection>
            <h3>Review Course</h3>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <h2 style={{ margin: '0 0 8px 0', color: 'white' }}>{courseDetails.title}</h2>
              <p style={{ margin: '0', opacity: 0.9, color: 'white' }}>{courseDetails.description || 'No description provided'}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>Grade</h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>{grades.find(g => g.id === courseDetails.grade_id)?.name || 'No Grade'}</p>
              </div>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>Subject</h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>{subjects.find(s => s.id === courseDetails.subject_id)?.name || 'No Subject'}</p>
              </div>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>Term</h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>{courseDetails.term}</p>
              </div>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>Pass Mark</h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>{courseDetails.pass_mark}%</p>
              </div>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '16px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>Type</h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary }}>{courseDetails.is_series ? 'Series' : 'Single Course'}</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div style={{ background: theme.colors.backgroundSecondary, padding: '20px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary }}>Videos ({videos.length})</h4>
                {videos.length > 0 ? videos.map((video, index) => (
                  <p key={video.id} style={{ margin: '4px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                    {index + 1}. {video.title}
                  </p>
                )) : <p style={{ margin: 0, color: theme.colors.textTertiary, fontSize: '14px' }}>No videos added</p>}
              </div>

              <div style={{ background: theme.colors.backgroundSecondary, padding: '20px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary }}>Files ({files.length})</h4>
                {files.length > 0 ? files.map((file, index) => (
                  <p key={file.id} style={{ margin: '4px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                    {index + 1}. {file.title}
                  </p>
                )) : <p style={{ margin: 0, color: theme.colors.textTertiary, fontSize: '14px' }}>No files added</p>}
              </div>

              <div style={{ background: theme.colors.backgroundSecondary, padding: '20px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }}>
                <h4 style={{ margin: '0 0 12px 0', color: theme.colors.textPrimary }}>Quiz Questions ({questions.length})</h4>
                {questions.length > 0 ? questions.map((question, index) => (
                  <p key={question.id} style={{ margin: '4px 0', color: theme.colors.textSecondary, fontSize: '14px' }}>
                    Q{index + 1}: {question.question_text.substring(0, 50)}{question.question_text.length > 50 ? '...' : ''}
                  </p>
                )) : <p style={{ margin: 0, color: theme.colors.textTertiary, fontSize: '14px' }}>No questions added</p>}
              </div>
            </div>
          </FormSection>
        );

      default:
        return null;
    }
  };

  if (loadingCourse) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <LoadingSpinner size="large" text="Loading course data..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>{editMode ? 'Edit Course ✏️' : 'Create Course 🎓'}</h1>
            <p>{editMode ? 'Update your course details' : 'Build your course step by step'}</p>
          </div>
        </DashboardHeader>

        <WizardContainer>
          <WizardSteps>
            <ProgressBar>
              <div style={{ 
                width: `${(currentStep / steps.length) * 100}%`,
                height: '4px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '2px',
                transition: 'width 0.3s ease'
              }} />
            </ProgressBar>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px' }}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.number} 
                  active={currentStep >= step.number} 
                  completed={currentStep > step.number}
                  onClick={() => !loadingCourse && setCurrentStep(step.number)}
                  style={{ cursor: loadingCourse ? 'not-allowed' : 'pointer' }}
                >
                  <div className="step-number">{step.number}</div>
                  <div className="step-info">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                </StepIndicator>
              ))}
            </div>
          </WizardSteps>

          <StepContent>
            {renderStepContent()}
          </StepContent>

          <WizardActions>
            <Button 
              variant="ghost" 
              onClick={prevStep} 
              disabled={currentStep === 1}
              leftIcon={<FiArrowLeft />}
            >
              Previous
            </Button>
            
            {currentStep < steps.length ? (
              <Button 
                onClick={nextStep}
                rightIcon={<FiArrowRight />}
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleCreateCourse}
                loading={loading}
                disabled={!editMode && questions.length < 5}
              >
                {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Course' : 'Create Course')}
              </Button>
            )}
          </WizardActions>
        </WizardContainer>

        {/* Delete Question Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setQuestionToDelete(null);
          }}
          title="Delete Question"
          size="small"
        >
          <div style={{ padding: '20px 0' }}>
            <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary }}>
              Are you sure you want to delete this question? This action cannot be undone.
            </p>
            {questionToDelete !== null && (
              <div style={{ 
                background: theme.colors.backgroundSecondary, 
                padding: '12px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: `1px solid ${theme.colors.border}`
              }}>
                <p style={{ margin: 0, fontWeight: '600', color: theme.colors.textPrimary }}>
                  Q{questionToDelete + 1}: {questions[questionToDelete]?.question_text}
                </p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setQuestionToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={confirmDeleteQuestion}
                style={{ background: '#EF4444' }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        {/* Video Preview Modal */}
        <Modal
          isOpen={showVideoPreview}
          onClose={closeVideoPreview}
          title={previewVideo?.title || 'Video Preview'}
          size="large"
        >
          <div style={{ padding: '20px 0' }}>
            {previewVideo?.file && (
              <video
                controls
                autoPlay
                style={{
                  width: '100%',
                  maxHeight: '60vh',
                  borderRadius: '8px',
                  background: '#000'
                }}
                src={URL.createObjectURL(previewVideo.file)}
              >
                Your browser does not support the video tag.
              </video>
            )}
            <div style={{ marginTop: '16px', color: theme.colors.textSecondary }}>
              <p style={{ margin: '8px 0', fontSize: '14px' }}>
                <strong>File Name:</strong> {previewVideo?.file?.name}
              </p>
              <p style={{ margin: '8px 0', fontSize: '14px' }}>
                <strong>File Size:</strong> {previewVideo?.file?.size ? (previewVideo.file.size / (1024 * 1024)).toFixed(2) + ' MB' : 'N/A'}
              </p>
            </div>
          </div>
        </Modal>

        {/* Full-screen loading overlay for uploads */}
        {uploading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
          }}>
            <div style={{
              background: theme.colors.backgroundPrimary,
              padding: '40px',
              borderRadius: '16px',
              boxShadow: theme.shadows.xl,
              textAlign: 'center',
              maxWidth: '400px',
              width: '90%',
            }}>
              <LoadingSpinner size="large" />
              <h3 style={{ 
                margin: '20px 0 10px 0', 
                color: theme.colors.textPrimary,
                fontSize: '20px',
                fontWeight: '600'
              }}>
                {editMode ? 'Updating Course...' : 'Creating Course...'}
              </h3>
              <p style={{ 
                color: theme.colors.textSecondary,
                margin: '0 0 20px 0',
                fontSize: '16px'
              }}>
                Please wait while we upload your videos and files. This may take a few minutes.
              </p>
              
              {/* Progress Bar */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: theme.colors.backgroundSecondary,
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #2c5f34, #8B5CF6)',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                  color: theme.colors.textTertiary
                }}>
                  <span>{uploadProgress === 100 ? 'Processing...' : 'Uploading...'}</span>
                  <span>{uploadProgress}%</span>
                </div>
              </div>
              <div style={{
                background: theme.colors.backgroundSecondary,
                padding: '16px',
                borderRadius: '8px',
                fontSize: '14px',
                color: theme.colors.textTertiary,
              }}>
                <div style={{ marginBottom: '8px' }}>
                  📹 Videos: {videos.length} file{videos.length !== 1 ? 's' : ''}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  📄 Files: {files.length} file{files.length !== 1 ? 's' : ''}
                </div>
                <div>
                  ❓ Quiz: {questions.length} question{questions.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
        )}
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default CourseCreationWizard;
